"use client";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LucideIcon, CheckCircle2 } from "lucide-react";
import { SERVICE_DETAILS } from "@/lib/services-data";

interface Service {
  icon: LucideIcon;
  title: string;
  description: string;
}

interface ServiceDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  service: Service | null;
  onBookingClick: () => void;
}

/**
 * ServiceDetailsModal Component
 * Displays detailed information about a selected service.
 * Includes sections for description, features, and a call to action.
 */
export function ServiceDetailsModal({
  open,
  onOpenChange,
  service,
  onBookingClick,
}: ServiceDetailsModalProps) {
  if (!service) return null;

  const Icon = service.icon;
  const details = SERVICE_DETAILS[service.title];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] gap-0 overflow-y-auto border-none p-0 shadow-2xl sm:max-w-[700px]"
        onOpenAutoFocus={(e) => e.preventDefault()}
        aria-describedby="service-description"
      >
        {/* Header with Icon and Title */}
        <div className="bg-primary/5 border-primary/10 border-b p-8">
          <div className="flex items-center gap-6">
            <div className="text-primary rounded-2xl bg-white p-4 shadow-sm">
              <Icon className="h-10 w-10" aria-hidden="true" />
            </div>
            <div>
              <DialogTitle className="text-foreground mb-1 text-3xl font-bold">
                {service.title}
              </DialogTitle>
              <p className="text-primary font-medium">Premium Treatment</p>
              {/* Hidden description for accessibility */}
              <DialogDescription id="service-description" className="sr-only">
                Detailed information about {service.title} service.
              </DialogDescription>
            </div>
          </div>
        </div>

        <div className="space-y-8 p-8">
          {/* Detailed Sections */}
          {details ? (
            <div className="space-y-8">
              {details.sections.map((section, idx) => (
                <div key={idx} className="space-y-3">
                  <h4 className="text-foreground flex items-center gap-2 text-xl font-bold">
                    <div
                      className="bg-primary h-6 w-1.5 rounded-full"
                      aria-hidden="true"
                    />
                    {section.title}
                  </h4>
                  <p className="text-muted-foreground text-lg leading-relaxed">
                    {section.content}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-muted-foreground text-lg leading-relaxed">
                {service.description}
              </p>
            </div>
          )}

          {/* Features List */}
          <div className="bg-secondary/30 border-border/50 grid grid-cols-1 gap-4 rounded-2xl border p-6 md:grid-cols-2">
            {[
              "Expert Doctors", // ✅ CHANGED — was "Expert Dermatologists"
              "FDA Approved Tech",
              "Personalized Plans",
              "Safe & Effective",
            ].map((feature) => (
              <div
                key={feature}
                className="text-foreground flex items-center gap-3 text-sm font-medium"
              >
                <CheckCircle2
                  className="text-primary h-5 w-5"
                  aria-hidden="true"
                />
                {feature}
              </div>
            ))}
          </div>

          {/* Footer Actions */}
          <div className="flex flex-col justify-end gap-4 pt-4 sm:flex-row">
            <Button
              variant="outline"
              size="lg"
              className="h-12 rounded-full px-8"
              onClick={() => onOpenChange(false)}
            >
              Close
            </Button>
            <Button
              size="lg"
              className="shadow-primary/20 h-12 rounded-full px-8 shadow-lg"
              onClick={() => {
                onOpenChange(false);
                onBookingClick();
              }}
            >
              Book Appointment
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
